import { FC, useContext, useState } from 'react';
import Card from '../Card/Card';
import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';
import { LoadingButton } from '@mui/lab';
import CoBranding from '../CoBranding/CoBranding';
import { toasterDurationMilliseconds } from '../../util/constants';
import ConfirmStaffAccountContext from '../../contexts/ConfirmStaffAccountContext';

enum ELocalUiState {
    default,
    loading,
    success,
    error,
}

const ConfirmStaffAccountExpired: FC = () => {
    const { language, coBranding, customerAccountId } = useContext(GlobalContext);
    const { resendOnboardingEmail } = useContext(ConfirmStaffAccountContext);
    const [localUiState, setLocalUiState] = useState(ELocalUiState.default);
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);

    const handleResendEmail = async () => {
        setLocalUiState(ELocalUiState.loading);
        try {
            await resendOnboardingEmail();
            setLocalUiState(ELocalUiState.success);
        } catch (error) {
            const { cause } = error;
            if (cause === 409) {
                setErrorMessage(language.validation.accountExists.header);
            } else if (cause === 429) {
                setErrorMessage(language.validation.accountLocked);
            } else {
                setErrorMessage(language.validation.genericError);
            }
            setLocalUiState(ELocalUiState.error);
        }
    };

    return (
        <>
            <Card>
                {(coBranding.logoUrl || coBranding.name) && (
                    <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
                )}
                {localUiState === ELocalUiState.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                <Stack alignItems={'center'} sx={{ py: 2 }}>
                    <Typography component="h1" variant="h4" sx={{ mb: 1 }}>
                        {language.confirmStaffAccount.expired.header}
                    </Typography>
                    <Typography data-testid={'verify-desc'}>
                        {customerAccountId
                            ? language.confirmStaffAccount.expired.desc
                            : language.confirmStaffAccount.expired.desc2}
                    </Typography>
                    {customerAccountId && (
                        <LoadingButton
                            variant="contained"
                            onClick={handleResendEmail}
                            fullWidth
                            size="large"
                            sx={{ mt: 2 }}
                            loading={localUiState === ELocalUiState.loading}
                        >
                            {language.confirmStaffAccount.expired.cta}
                        </LoadingButton>
                    )}
                </Stack>
            </Card>
            <Snackbar
                open={localUiState === ELocalUiState.success}
                autoHideDuration={toasterDurationMilliseconds}
                onClose={() => setLocalUiState(ELocalUiState.default)}
            >
                <Alert severity="success">{language.confirmStaffAccount.expired.success}</Alert>
            </Snackbar>
        </>
    );
};

export default ConfirmStaffAccountExpired;
